"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { renderSample } from "@/lib/resumeRender/catalog";
import { RESUME_ROOT_STYLE, usePagedLayout } from "@/lib/resumeRender/layout";
import type { ResumeSample } from "@/lib/resumeRender/model";
import { unpaginated } from "@/lib/resumeRender/paginate";
import { PT_TO_PX, ResumePage } from "@/lib/resumeRender/renderHtml";
import "./resume-fonts.css";

// A live HTML rendering of one sample in one template — the same pages the
// PDF download draws (src/lib/resumeRender). Pages are laid out at real size
// and scaled down, so line breaks never depend on the preview size. Before
// the fonts are measured (and on the server) the first page is the design
// clipped to one page; it is replaced by the paginated pages once measured.

type Props = {
  sample: ResumeSample;
  templateId: string;
  year: number;
  /**
   * Frame width in px. Fixed widths render at the right scale on the server;
   * omit it to fill the parent's width (measured after mount).
   */
  width?: number;
  /** Show every page, stacked, instead of just the first. */
  full?: boolean;
  /**
   * Render only in the browser, once near the viewport. A preview's markup is
   * ~45 KB of HTML, so thumbnails beyond the hero would otherwise more than
   * double every page.
   */
  lazy?: boolean;
  className?: string;
  label?: string;
};

const PAGE_GAP = 16;

export default function ResumePreview({ sample, templateId, year, width, full = false, lazy = false, className, label }: Props) {
  const spec = useMemo(() => renderSample(sample, templateId, year), [sample, templateId, year]);
  const frameRef = useRef<HTMLDivElement>(null);
  const [measured, setMeasured] = useState<number | null>(null);
  const [visible, setVisible] = useState(!lazy);
  const { doc, host } = usePagedLayout(visible ? spec : null);
  const shown = doc && doc.spec === spec ? doc : unpaginated(spec);

  useEffect(() => {
    if (visible) return;
    const el = frameRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "400px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible]);

  useEffect(() => {
    if (width !== undefined) return;
    const el = frameRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => setMeasured(entry.contentRect.width));
    observer.observe(el);
    return () => observer.disconnect();
  }, [width]);

  const pageW = spec.size.width * PT_TO_PX;
  const pageH = spec.size.height * PT_TO_PX;
  const frameWidth = width ?? measured;
  const scale = frameWidth ? frameWidth / pageW : 0;
  const pages = full ? shown.pages : shown.pages.slice(0, 1);
  const height = scale ? pages.length * pageH * scale + (pages.length - 1) * PAGE_GAP : undefined;

  return (
    <div
      ref={frameRef}
      role="img"
      aria-label={label}
      className={full ? undefined : className}
      style={{
        position: "relative",
        overflow: full ? "visible" : "hidden",
        width: width ?? "100%",
        height,
        aspectRatio: height ? undefined : `${pageW} / ${pageH}`,
        background: full ? undefined : "#fff",
      }}
    >
      {visible && scale
        ? pages.map((_, i) => (
            <div
              key={i}
              className={full ? className : undefined}
              style={{
                position: "absolute",
                left: 0,
                top: i * (pageH * scale + PAGE_GAP),
                width: pageW * scale,
                height: pageH * scale,
                overflow: "hidden",
                background: "#fff",
              }}
            >
              <div
                aria-hidden="true"
                style={{
                  ...RESUME_ROOT_STYLE,
                  width: pageW,
                  transform: `scale(${scale})`,
                  transformOrigin: "top left",
                  pointerEvents: "none",
                  userSelect: "none",
                }}
              >
                <ResumePage doc={shown} index={i} />
              </div>
            </div>
          ))
        : null}
      {host}
    </div>
  );
}
